import React, { useState, useEffect, useCallback } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Box, Flex, Text, Group, TextInput, Button, Pagination, Modal, Select, ScrollArea, Card } from '@mantine/core'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX, IconPlus, IconSearch, IconShoppingCart } from '@tabler/icons-react'
import { modals } from '@mantine/modals'

import {
  getInventories, getWarehouses, getQuantityUnits, deleteInventoryItem,
  getMainCategories, getDimensionUnits, getWeightUnits
} from '../../../../services/inventory'
import TableInventory from '../../../../component/pages/Inventory/TableInventory'
import FormInventory from '../../../../component/pages/Inventory/FormInventory'
import FormMovement from '../../../../component/pages/Inventory/FormMovement'
import MovementHistory from '../../../../component/pages/Inventory/MovementHistory'

const Inventory = () => {
  const dispatch = useDispatch()

  // Search & filter params
  const [params, setParams] = useState({ productName: '', warehouseId: 0, page: 1, limit: 10 })
  const [inventories, setInventories] = useState([])
  const [totalPages, setTotalPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)

  // Master Data (needed by forms)
  const [warehouseList, setWarehouseList] = useState([])
  const [quantityUnitList, setQuantityUnitList] = useState([])
  const [dimensionUnitList, setDimensionUnitList] = useState([])
  const [weightUnitList, setWeightUnitList] = useState([])
  const [mainCategoryList, setMainCategoryList] = useState([])

  // Modals
  const [openInventoryModal, setOpenInventoryModal] = useState(false)
  const [editInventoryId, setEditInventoryId] = useState(null)

  const [openMovementModal, setOpenMovementModal] = useState(false)

  const [openHistoryModal, setOpenHistoryModal] = useState(false)
  const [historyInventory, setHistoryInventory] = useState({ id: 0, productName: '' })

  // Order items (movement)
  const [orderItems, setOrderItems] = useState([])
  const [orderWarehouseId, setOrderWarehouseId] = useState(0)
  const [orderWarehouseName, setOrderWarehouseName] = useState('')

  const mappingRoute = [
    { label: '/', route: null },
    { label: 'Fulfillment', route: null },
    { label: 'Inventory', route: null },
  ]

  // ====== Fetch inventories ======
  const fetchInventories = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getInventories(params)
      const invData = res?.data?.inventories
      if (invData) {
        const mapped = (invData.data || []).map(inv => ({
          id: inv.id,
          description: inv.description || '-',
          sku: inv.sku,
          productName: inv.productName,
          quantity: inv.specification?.quantity,
          quantityUnit: inv.specification?.quantityUnit,
          category: inv.categories && inv.categories.length > 0 ? inv.categories[0].name : '-',
          warehouse: inv.warehouse?.name || '',
          warehouseId: Number(inv.warehouse?.id),
          owner: { id: inv.user?.id || null, name: inv.user?.name || '' },
          creator: { id: inv.creator?.id || null, name: inv.creator?.name || '' },
          editor: { id: inv.editor?.id || null, name: inv.editor?.name || '' },
          createdAt: inv.createdAt,
          updatedAt: inv.updatedAt,
        }))
        setInventories(mapped)
        const pi = invData.paginatorInfo
        setCurrentPage(pi.currentPage)
        setTotalPages(Math.ceil(pi.total / params.limit))
      }
    } catch (error) {
      notifications.show({ title: 'Error', message: error.message, color: 'red', icon: <IconX /> })
    } finally {
      setLoading(false)
    }
  }, [params])

  const fetchMasterData = useCallback(async () => {
    try {
      const [whRes, catRes, quRes, duRes, wuRes] = await Promise.all([
        getWarehouses(), getMainCategories(), getQuantityUnits(), getDimensionUnits(), getWeightUnits()
      ])
      setWarehouseList(whRes?.data?.warehouses?.data || [])
      setMainCategoryList((catRes?.data?.categories?.data || []).map(c => ({ id: c.id, name: c.name })))
      setQuantityUnitList((quRes?.data?.quantityUnit || []).map(q => ({ label: q.label, name: q.name, value: q.value })))
      setDimensionUnitList((duRes?.data?.lengthUnit || []).map(d => ({ label: d.label, name: d.name, value: d.value })))
      setWeightUnitList((wuRes?.data?.weightUnit || []).map(w => ({ label: w.label, name: w.name, value: w.value })))
    } catch (error) {
      console.error('Failed to fetch master data', error)
    }
  }, [])

  useEffect(() => {
    dispatch(updateRoute({ 'data': mappingRoute }))
    fetchMasterData()
    // eslint-disable-next-line
  }, [dispatch])

  useEffect(() => {
    fetchInventories()
  }, [fetchInventories])

  // ====== Actions ======
  const handleSearch = () => {
    setParams(prev => ({ ...prev, page: 1 }))
  }

  const handleClear = () => {
    setParams({ productName: '', warehouseId: 0, page: 1, limit: 10 })
  }

  const handleChangePage = (page) => {
    setParams(prev => ({ ...prev, page }))
  }

  const handleOpenAddSku = () => {
    setEditInventoryId(null)
    setOpenInventoryModal(true)
  }

  const handleOpenEditSku = (id) => {
    setEditInventoryId(Number(id))
    setOpenInventoryModal(true)
  }

  const handleCloseInventoryModal = () => {
    setOpenInventoryModal(false)
    setEditInventoryId(null)
  }

  const handleDelete = (item) => {
    modals.openConfirmModal({
      title: 'Delete Inventory',
      children: (
        <Text size="sm">Are you sure you want to delete SKU <strong>{item.sku}</strong>? This action cannot be undone.</Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          const res = await deleteInventoryItem(item.id)
          if (res?.data?.inventoryDelete) {
            notifications.show({ title: 'Success', message: 'Data successfully deleted', color: 'teal', icon: <IconCheck /> })
            setOrderItems(prev => prev.filter(e => Number(e.id) !== Number(item.id)))
            fetchInventories()
          } else {
            notifications.show({ title: 'Failed', message: 'Data cannot be deleted', color: 'red', icon: <IconX /> })
          }
        } catch (error) {
          notifications.show({ title: 'Error', message: error.message, color: 'red', icon: <IconX /> })
        }
      },
    })
  }

  const handleMovementHistory = (item) => {
    setHistoryInventory({ id: Number(item.id), productName: item.productName })
    setOpenHistoryModal(true)
  }

  const goToUserDetail = (userId) => {
    window.open(`/users/${userId}`, '_blank')
  }

  // ====== Order / Movement ======
  const addToOrder = (item) => {
    const { id, productName, quantityUnit, warehouse, warehouseId } = item
    if (orderItems.length > 0) {
      if (warehouseId === orderWarehouseId) {
        setOrderItems(prev => [...prev, { id, productName, quantity: 1, quantityUnit }])
      } else {
        notifications.show({ title: 'Error', message: 'Cannot add to Order with different Warehouse', color: 'red', icon: <IconX /> })
      }
    } else {
      setOrderItems([{ id, productName, quantity: 1, quantityUnit }])
      setOrderWarehouseId(Number(warehouseId))
      setOrderWarehouseName(warehouse)
    }
  }

  const removeFromOrder = (idInventory) => {
    setOrderItems(prev => prev.filter(e => Number(e.id) !== Number(idInventory)))
  }

  const updateOrderItemQty = (idInventory, qty) => {
    setOrderItems(prev => prev.map(item => Number(item.id) === Number(idInventory) ? { ...item, quantity: qty } : item))
  }

  const handleRefresh = () => {
    handleClear()
    setOrderItems([])
    setOrderWarehouseId(0)
    setOrderWarehouseName('')
    fetchInventories()
    fetchMasterData()
  }

  return (
    <AuthLayout>
      <Box my={20}>
        <Flex justify='space-between' align="center" mb={30}>
          <Text className={classes.titleLayout}>Inventory List</Text>
        </Flex>

        <Card withBorder shadow="sm" radius="md" p="lg">
          {/* Toolbar */}
          <Flex justify="space-between" mb="md" wrap="wrap" gap="sm">
            <Group gap="sm">
              <Select
                size="xs"
                placeholder="All Warehouse"
                clearable
                data={[
                  { value: '0', label: 'All Warehouse' },
                  ...warehouseList.map(w => ({ value: String(w.id), label: w.name }))
                ]}
                value={String(params.warehouseId)}
                onChange={(v) => setParams(prev => ({ ...prev, warehouseId: Number(v || 0), page: 1 }))}
                w={180}
              />
              <TextInput
                size="xs"
                placeholder="Product Name"
                value={params.productName}
                onChange={(e) => setParams(prev => ({ ...prev, productName: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
                leftSection={<IconSearch size={14} />}
              />
              <Button size="xs" onClick={handleSearch}>Search</Button>
              <Button size="xs" variant="default" onClick={handleClear}>Clear</Button>
            </Group>
            <Group gap="sm">
              <Button size="xs" leftSection={<IconShoppingCart size={14} />} variant="light"
                onClick={() => setOpenMovementModal(true)}>
                Order ({orderItems.length})
              </Button>
              <Button size="xs" leftSection={<IconPlus size={14} />} onClick={handleOpenAddSku}>
                Add New SKU
              </Button>
            </Group>
          </Flex>

          {/* Table */}
          <TableInventory
            data={inventories}
            loading={loading}
            onEdit={handleOpenEditSku}
            onDelete={handleDelete}
            onMovementHistory={handleMovementHistory}
            onGoToUser={goToUserDetail}
            orderItems={orderItems}
            onAddToOrder={addToOrder}
            onRemoveFromOrder={removeFromOrder}
          />

          {/* Pagination */}
          <Flex justify="space-between" align="center" mt="md">
            <Text c="dimmed" size="sm">Page {currentPage}</Text>
            <Pagination size="sm" value={currentPage} total={totalPages} onChange={handleChangePage} />
          </Flex>
        </Card>
      </Box>

      {/* Add / Edit Inventory Modal */}
      <Modal
        opened={openInventoryModal}
        onClose={handleCloseInventoryModal}
        title={<Text fw="bold">{editInventoryId ? 'Edit SKU' : 'Add New SKU'}</Text>}
        size="xl"
        centered
        scrollAreaComponent={ScrollArea.Autosize}
      >
        <FormInventory
          idSelected={editInventoryId}
          onClose={handleCloseInventoryModal}
          onRefresh={handleRefresh}
          warehouseList={warehouseList}
          quantityUnitList={quantityUnitList}
          dimensionUnitList={dimensionUnitList}
          weightUnitList={weightUnitList}
          mainCategoryList={mainCategoryList}
        />
      </Modal>

      {/* Movement Order Modal */}
      <Modal
        opened={openMovementModal}
        onClose={() => setOpenMovementModal(false)}
        title={<Text fw="bold">Order Movement</Text>}
        size="lg"
        centered
        scrollAreaComponent={ScrollArea.Autosize}
      >
        <FormMovement
          items={orderItems}
          warehouseName={orderWarehouseName}
          warehouseId={orderWarehouseId}
          quantityUnitList={quantityUnitList}
          onRemoveItem={removeFromOrder}
          onUpdateItemQty={updateOrderItemQty}
          onClose={() => setOpenMovementModal(false)}
          onRefresh={handleRefresh}
        />
      </Modal>

      {/* Movement History Modal */}
      <Modal
        opened={openHistoryModal}
        onClose={() => { setOpenHistoryModal(false); setHistoryInventory({ id: 0, productName: '' }) }}
        title={<Text fw="bold">Movement Histories</Text>}
        size="90%"
        centered
        scrollAreaComponent={ScrollArea.Autosize}
      >
        <MovementHistory
          inventoryId={historyInventory.id}
          productName={historyInventory.productName}
          onGoToUser={goToUserDetail}
        />
      </Modal>
    </AuthLayout>
  )
}

export default Inventory